import { useState } from "react";
import { ChevronDown, Move } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { TALKING_AVATAR_MAX_BG_CHARS } from "@/pages/TalkingAvatar/constants";

interface TalkingAvatarDialogueInputProps {
  /** Display number — shifts with the chosen image mode path. */
  stepNumber: number;
  dialogue: string;
  motion: string;
  /** Word cap for the CURRENT user (60 Free / 90 Pro). Comes from the
   *  /voices endpoint so it tracks plan changes without a redeploy. */
  maxWords: number;
  /** Word floor (default 4). Below this Kling rejects the audio for being
   *  shorter than its 2-second minimum. Also served from /voices. */
  minWords: number;
  onDialogueChange: (value: string) => void;
  onMotionChange: (value: string) => void;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function TalkingAvatarDialogueInput({
  stepNumber,
  dialogue,
  motion,
  maxWords,
  minWords,
  onDialogueChange,
  onMotionChange,
}: TalkingAvatarDialogueInputProps) {
  const { t } = useLanguage();
  const wordCount = countWords(dialogue);
  const overLimit = wordCount > maxWords;
  // Flag under-limit even on an empty textarea: the voice step stays locked
  // until the floor is met, so this red hint is the user's only cue for why
  // the next step hasn't appeared yet.
  const underLimit = wordCount < minWords;
  const offLimit = overLimit || underLimit;
  // Auto-open if motion already has content (returning to step 6 with prior input).
  const [motionOpen, setMotionOpen] = useState(motion.length > 0);

  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.talkingAvatar.stepPrefix.replace("{n}", String(stepNumber))}
          {t.talkingAvatar.dialogueTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.talkingAvatar.dialogueHint}
        </p>
      </div>

      <div>
        <label className="block mb-2 text-sm font-medium text-foreground">
          {t.talkingAvatar.dialogueLabel}
        </label>
        <textarea
          value={dialogue}
          onChange={(e) => onDialogueChange(e.target.value)}
          placeholder={t.talkingAvatar.dialoguePlaceholder}
          rows={3}
          className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none"
        />
        <p
          className={`mt-1.5 text-[11px] text-end ${offLimit ? "text-destructive" : "text-muted-foreground/80"}`}
        >
          {t.talkingAvatar.wordCountLabel
            .replace("{count}", String(wordCount))
            .replace("{max}", String(maxWords))}
        </p>
        {underLimit && (
          // The voice step stays locked until the word floor is met, so this
          // is the user's only cue — bold red (explicit red-600: the theme's
          // `destructive` token is a muted brown, not an attention red),
          // aligned to the reading start edge (right in RTL, left in LTR).
          <p className="mt-1 text-xs md:text-sm font-bold text-red-600 text-start">
            {t.talkingAvatar.minWordsHint.replace("{min}", String(minWords))}
          </p>
        )}
      </div>

      {/* Optional motion / action prompt — forwarded to Kling Avatar's
          positivePrompt so the character can move, gesture, or emote while
          speaking. Hidden behind a toggle to keep the default flow simple. */}
      <div>
        <button
          type="button"
          onClick={() => setMotionOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          aria-expanded={motionOpen}
        >
          <Move className="w-4 h-4" />
          <span>
            {motionOpen
              ? t.talkingAvatar.motionToggleHide
              : t.talkingAvatar.motionToggleShow}
          </span>
          <ChevronDown
            className={cn(
              "w-4 h-4 transition-transform",
              motionOpen && "rotate-180",
            )}
          />
        </button>
        {motionOpen && (
          <div className="mt-3 rounded-xl border border-border/60 bg-card/50 p-3 md:p-4 animate-in fade-in slide-in-from-top-1 duration-200">
            <label className="block mb-2 text-sm font-medium text-foreground">
              {t.talkingAvatar.motionLabel}
            </label>
            <textarea
              value={motion}
              onChange={(e) => onMotionChange(e.target.value)}
              placeholder={t.talkingAvatar.motionPlaceholder}
              rows={2}
              maxLength={TALKING_AVATAR_MAX_BG_CHARS}
              className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none"
            />
            <div className="mt-1.5 flex justify-between text-[11px] text-muted-foreground/80">
              <span>{t.talkingAvatar.motionHint}</span>
              <span>
                {motion.length} / {TALKING_AVATAR_MAX_BG_CHARS}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
